import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const WHATSAPP_BACK_URL = 'https://wa.me/919315918562';

const emptyDetails = {
  candidate_name: '',
  mobile: '',
  age: '',
  gender: '',
  qualification: '',
  trade: '',
  home_location: '',
  work_location: '', // state derived from active job locations
};

function draftKey(supervisorId) {
  return `jobInterestDraft:${supervisorId}`;
}

function readDraft(supervisorId) {
  try {
    const raw = sessionStorage.getItem(draftKey(supervisorId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeDraft(supervisorId, draft) {
  try {
    sessionStorage.setItem(draftKey(supervisorId), JSON.stringify(draft));
  } catch {
    /* ignore quota / private mode */
  }
}

function JobInterestForm() {
  const { supervisorId } = useParams();
  const navigate = useNavigate();
  const initialDraft = useMemo(() => readDraft(supervisorId), [supervisorId]);
  const [loading, setLoading] = useState(true);
  const [supervisor, setSupervisor] = useState(null);
  const [states, setStates] = useState([]);
  const [step, setStep] = useState(() => {
    if (initialDraft?.step === 'jobs' && initialDraft?.details?.work_location) return 'jobs';
    if (initialDraft?.step === 'done' && initialDraft?.details?.work_location) return 'jobs';
    return 'details';
  });
  const [details, setDetails] = useState(() => ({
    ...emptyDetails,
    ...(initialDraft?.details || {}),
  }));
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [search, setSearch] = useState(() => initialDraft?.search || '');
  const [selectedJobIds, setSelectedJobIds] = useState(() =>
    Array.isArray(initialDraft?.selectedJobIds) ? initialDraft.selectedJobIds : []
  );
  const [submitting, setSubmitting] = useState(false);
  const [contacts, setContacts] = useState(null);
  const jobsTopRef = useRef(null);
  const skipScrollOnce = useRef(Boolean(initialDraft?.step === 'jobs'));

  // Keep draft so job-detail → back restores jobs step (component remounts otherwise)
  useEffect(() => {
    writeDraft(supervisorId, {
      step: step === 'done' ? 'jobs' : step,
      details,
      selectedJobIds,
      search,
    });
  }, [supervisorId, step, details, selectedJobIds, search]);

  const loadJobs = useCallback(async (state, q) => {
    setJobsLoading(true);
    try {
      const params = new URLSearchParams();
      if (state) params.set('state', state);
      if (q) params.set('q', q);
      const qs = params.toString();
      const res = await fetch(
        `${API_URL}/api/job-interest/${supervisorId}/jobs${qs ? `?${qs}` : ''}`
      );
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message || 'Failed to load jobs');
        setJobs([]);
        return;
      }
      setJobs(data.jobs || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load jobs');
    } finally {
      setJobsLoading(false);
    }
  }, [supervisorId]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/job-interest/${supervisorId}/meta`);
        const data = await res.json();
        if (!data.success) {
          toast.error(data.message || 'Invalid link');
          setSupervisor(null);
          return;
        }
        setSupervisor(data.supervisor);
        setStates(data.states || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [supervisorId]);

  useEffect(() => {
    if (step !== 'jobs' || !supervisor || !details.work_location) return;
    const t = window.setTimeout(() => {
      loadJobs(details.work_location, search.trim());
    }, search ? 220 : 0);
    return () => window.clearTimeout(t);
  }, [step, supervisor, details.work_location, search, loadJobs]);

  useEffect(() => {
    if (step !== 'jobs') return;
    if (skipScrollOnce.current) {
      skipScrollOnce.current = false;
      return;
    }
    window.setTimeout(() => {
      jobsTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, [step]);

  const selectedJobs = useMemo(
    () => jobs.filter((j) => selectedJobIds.includes(j.id)),
    [jobs, selectedJobIds]
  );

  const onDetailsChange = (e) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  const goToJobs = (e) => {
    e.preventDefault();
    if (!details.candidate_name.trim()) return toast.error('Name is required');
    const digits = details.mobile.replace(/\D/g, '');
    if (digits.length < 10) return toast.error('Enter a valid 10-digit mobile');
    if (!details.work_location) return toast.error('Select work location (state)');
    const nextDetails = { ...details, mobile: digits.slice(-10) };
    const prevDraft = readDraft(supervisorId);
    const stateChanged = prevDraft?.details?.work_location !== nextDetails.work_location;
    setDetails(nextDetails);
    if (stateChanged) {
      setSelectedJobIds([]);
      setSearch('');
    }
    setStep('jobs');
  };

  const toggleJob = (id, e) => {
    if (e) e.stopPropagation();
    setSelectedJobIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openJob = (jobId) => {
    const draft = {
      step: 'jobs',
      details,
      selectedJobIds,
      search,
    };
    writeDraft(supervisorId, draft);
    try {
      sessionStorage.setItem(`jobInterestFromList:${supervisorId}`, '1');
    } catch {
      /* ignore */
    }
    navigate(`/job-interest/${supervisorId}/job/${jobId}`, {
      state: {
        from: 'list',
        prefill: {
          candidate_name: details.candidate_name.trim(),
          mobile: details.mobile.replace(/\D/g, '').slice(-10),
          age: details.age,
          gender: details.gender,
          qualification: details.qualification,
          trade: details.trade,
          home_location: details.home_location,
          work_location: details.work_location,
        },
      },
    });
  };

  const onSubmit = async () => {
    if (!selectedJobIds.length) return toast.error('Select at least one job');
    // Persist chosen state; site detail stays on each selected job
    const workLocation = details.work_location || selectedJobs[0]?.location || '';

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/job-interest/${supervisorId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate_name: details.candidate_name.trim(),
          mobile: details.mobile.replace(/\D/g, '').slice(-10),
          age: details.age,
          gender: details.gender,
          qualification: details.qualification,
          trade: details.trade,
          home_location: details.home_location,
          work_location: workLocation,
          selected_job_ids: selectedJobIds,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.message || 'Submit failed');
        return;
      }
      setContacts(data.contacts || []);
      setSelectedJobIds([]);
      setStep('done');
      toast.success(data.merged ? 'Application updated' : 'Applied successfully');
    } catch (err) {
      console.error(err);
      toast.error('Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.shell}>
          <div style={styles.loadingCard}>
            <div style={styles.spinner} />
            <p style={{ margin: '12px 0 0', color: '#64748b' }}>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!supervisor) {
    return (
      <div style={styles.page}>
        <Toaster position="top-center" />
        <div style={styles.shell}>
          <div style={styles.emptyCard}>
            <h1 style={styles.heroTitle}>Link not valid</h1>
            <p style={styles.sub}>Ask your supervisor for a fresh WhatsApp job link.</p>
            <a href={WHATSAPP_BACK_URL} style={styles.primaryLinkBtn}>Back to WhatsApp</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <Toaster position="top-center" />
      <div style={styles.shell}>
        <header style={styles.topBar}>
          {step === 'jobs' ? (
            <button type="button" onClick={() => setStep('details')} style={styles.backBtn}>
              ← Edit details
            </button>
          ) : (
            <a href={WHATSAPP_BACK_URL} style={styles.backBtn}>← WhatsApp</a>
          )}
          <span style={styles.topBadge}>
            {step === 'details' ? 'Step 1 of 2' : step === 'jobs' ? 'Step 2 of 2' : 'Done'}
          </span>
        </header>

        <div style={styles.heroCard}>
          <div style={styles.heroGlow} />
          <h1 style={styles.heroTitle}>
            {step === 'done' ? 'Application sent' : step === 'jobs' ? 'Choose jobs' : 'Your details'}
          </h1>
          <p style={styles.heroSub}>
            Shared by <strong>{supervisor.name}</strong>
            {step === 'details'
              ? ' — fill your details first, then pick the jobs you want.'
              : step === 'jobs'
                ? ` — applying as ${details.candidate_name}`
                : ` — thank you, ${details.candidate_name}`}
          </p>
          {step !== 'done' ? (
            <div style={styles.stepsRow}>
              <div style={{ ...styles.stepPill, ...(step === 'details' ? styles.stepPillActive : styles.stepPillDone) }}>
                1. Details
              </div>
              <div style={styles.stepLine} />
              <div style={{ ...styles.stepPill, ...(step === 'jobs' ? styles.stepPillActive : {}) }}>
                2. Jobs
              </div>
            </div>
          ) : null}
        </div>

        {step === 'details' ? (
          <form onSubmit={goToJobs} style={styles.card}>
            <label style={styles.label}>Full name *</label>
            <input
              name="candidate_name"
              value={details.candidate_name}
              onChange={onDetailsChange}
              style={styles.input}
              placeholder="Your name"
              autoComplete="name"
              required
            />

            <label style={styles.label}>Mobile number *</label>
            <input
              name="mobile"
              value={details.mobile}
              onChange={onDetailsChange}
              style={styles.input}
              placeholder="10-digit mobile"
              inputMode="tel"
              autoComplete="tel"
              required
            />

            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Age</label>
                <input name="age" value={details.age} onChange={onDetailsChange} style={styles.input} inputMode="numeric" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Gender</label>
                <select name="gender" value={details.gender} onChange={onDetailsChange} style={styles.input}>
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <label style={styles.label}>Qualification</label>
            <input
              name="qualification"
              value={details.qualification}
              onChange={onDetailsChange}
              style={styles.input}
              placeholder="e.g. ITI / Diploma / 12th"
            />

            <label style={styles.label}>Trade</label>
            <input name="trade" value={details.trade} onChange={onDetailsChange} style={styles.input} />

            <label style={styles.label}>Home location</label>
            <input
              name="home_location"
              value={details.home_location}
              onChange={onDetailsChange}
              style={styles.input}
              placeholder="City / area"
            />

            <label style={styles.label}>Work location (state) *</label>
            <select
              name="work_location"
              value={details.work_location}
              onChange={onDetailsChange}
              style={styles.input}
              required
            >
              <option value="">Select state</option>
              {states.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            <p style={styles.fieldHint}>
              Only states where we currently have open jobs are listed.
            </p>

            <button type="submit" style={styles.primaryBtn} disabled={!states.length}>
              Next — view jobs
            </button>
          </form>
        ) : null}

        {step === 'jobs' ? (
          <>
            <div ref={jobsTopRef} style={styles.profileChip}>
              <div>
                <strong>{details.candidate_name}</strong>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  {details.mobile} · {details.work_location}
                </div>
              </div>
              <button type="button" onClick={() => setStep('details')} style={styles.editLink}>
                Edit
              </button>
            </div>

            <div style={styles.filters}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search company, role, salary…"
                style={styles.searchInput}
                inputMode="search"
              />
              <div style={styles.stateBanner}>
                Showing jobs in <strong>{details.work_location}</strong>
              </div>
            </div>

            {jobsLoading ? (
              <div style={styles.hintCard}>Loading jobs…</div>
            ) : jobs.length === 0 ? (
              <div style={styles.hintCard}>No openings match your filters.</div>
            ) : (
              <div style={styles.jobList}>
                {jobs.map((job) => {
                  const checked = selectedJobIds.includes(job.id);
                  return (
                    <article
                      key={job.id}
                      style={{
                        ...styles.jobCard,
                        borderColor: checked ? '#0d9488' : '#e2e8f0',
                        background: checked ? '#f0fdfa' : '#fff',
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => toggleJob(job.id, e)}
                        style={{
                          ...styles.checkBtn,
                          background: checked ? '#0d9488' : '#fff',
                          borderColor: checked ? '#0d9488' : '#cbd5e1',
                          color: checked ? '#fff' : '#94a3b8',
                        }}
                        aria-label={checked ? 'Deselect job' : 'Select job'}
                      >
                        {checked ? '✓' : ''}
                      </button>
                      <button type="button" onClick={() => openJob(job.id)} style={styles.jobBody}>
                        <div style={styles.jobCompany}>{job.company_name}</div>
                        <div style={styles.jobPost}>{job.job_post}</div>
                        <div style={styles.jobLoc}>📍 {job.location}</div>
                        <div style={styles.jobMeta}>
                          {[job.qualification, job.salary, job.hours].filter(Boolean).join(' · ')}
                        </div>
                        <div style={styles.viewDetails}>View details →</div>
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
            <div style={{ height: 96 }} />
          </>
        ) : null}

        {step === 'done' && contacts ? (
          <section style={styles.successBox}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={{ ...styles.sectionTitle, marginBottom: 6 }}>You&apos;re applied!</h2>
            <p style={{ margin: '0 0 14px', color: '#134e4a', fontSize: 14 }}>
              Contact persons for your selected jobs:
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              {contacts.map((c) => (
                <div key={c.job_id} style={styles.contactCard}>
                  <div style={{ fontWeight: 800 }}>{c.company_name}</div>
                  <div style={{ fontSize: 13, color: '#475569' }}>{c.job_post}</div>
                  <div style={{ marginTop: 8, fontWeight: 700 }}>
                    {c.contact_person_name || 'Contact'}
                    {c.contact_person_number ? (
                      <>
                        {' · '}
                        <a href={`tel:${c.contact_person_number}`} style={styles.phoneInline}>
                          {c.contact_person_number}
                        </a>
                      </>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setContacts(null);
                setStep('jobs');
              }}
              style={{ ...styles.outlineBtn, marginTop: 14, width: '100%' }}
            >
              Apply to more jobs
            </button>
            <a
              href={WHATSAPP_BACK_URL}
              style={{ ...styles.primaryLinkBtn, marginTop: 10, display: 'block', textAlign: 'center' }}
            >
              Back to WhatsApp
            </a>
          </section>
        ) : null}
      </div>

      {step === 'jobs' ? (
        <div style={styles.stickyBar}>
          <button
            type="button"
            onClick={() => setSelectedJobIds([])}
            style={styles.stickySecondary}
            disabled={!selectedJobIds.length}
          >
            Clear
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting || !selectedJobIds.length}
            style={styles.stickyPrimary}
          >
            {submitting
              ? 'Submitting…'
              : selectedJobIds.length
                ? `Apply to ${selectedJobIds.length} job${selectedJobIds.length === 1 ? '' : 's'}`
                : 'Select jobs to apply'}
          </button>
        </div>
      ) : null}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #0f766e 0%, #0f766e 160px, #f1f5f9 160px, #f8fafc 100%)',
    padding: '0 0 24px',
    boxSizing: 'border-box',
    fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif',
  },
  shell: { maxWidth: 480, margin: '0 auto', padding: '0 16px' },
  topBar: {
    padding: '14px 0 8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backBtn: {
    border: 'none',
    background: 'rgba(255,255,255,0.2)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
    padding: '8px 14px',
    borderRadius: 999,
    textDecoration: 'none',
    cursor: 'pointer',
  },
  topBadge: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  heroCard: {
    position: 'relative',
    background: '#fff',
    borderRadius: 24,
    padding: '22px 20px 18px',
    marginBottom: 14,
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.12)',
    overflow: 'hidden',
  },
  heroGlow: {
    position: 'absolute',
    top: -40,
    right: -40,
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #99f6e4, #ccfbf1)',
    opacity: 0.6,
  },
  heroTitle: {
    margin: 0,
    fontSize: 26,
    lineHeight: 1.15,
    color: '#0f172a',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  },
  heroSub: { margin: '10px 0 0', fontSize: 14, color: '#64748b', lineHeight: 1.45 },
  stepsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
  },
  stepPill: {
    padding: '7px 12px',
    borderRadius: 999,
    background: '#f1f5f9',
    color: '#64748b',
    fontSize: 12,
    fontWeight: 800,
  },
  stepPillActive: { background: '#0d9488', color: '#fff' },
  stepPillDone: { background: '#ccfbf1', color: '#0f766e' },
  stepLine: { flex: 1, height: 2, background: '#e2e8f0', borderRadius: 2 },
  card: {
    background: '#fff',
    borderRadius: 20,
    padding: 20,
    boxShadow: '0 8px 28px rgba(15, 23, 42, 0.08)',
    marginBottom: 12,
  },
  label: {
    display: 'block',
    marginTop: 12,
    marginBottom: 6,
    fontSize: 13,
    fontWeight: 700,
    color: '#334155',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    fontSize: 16,
    background: '#f8fafc',
  },
  row: { display: 'flex', gap: 10 },
  primaryBtn: {
    marginTop: 18,
    width: '100%',
    padding: '16px',
    border: 'none',
    borderRadius: 16,
    background: 'linear-gradient(135deg, #0d9488, #0f766e)',
    color: '#fff',
    fontWeight: 800,
    fontSize: 16,
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(13, 148, 136, 0.35)',
  },
  profileChip: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#fff',
    borderRadius: 16,
    padding: '12px 14px',
    marginBottom: 12,
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.05)',
  },
  editLink: {
    border: 'none',
    background: 'transparent',
    color: '#0d9488',
    fontWeight: 800,
    cursor: 'pointer',
    fontSize: 13,
  },
  filters: { display: 'grid', gap: 8, marginBottom: 12 },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '14px 16px',
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    fontSize: 16,
    background: '#fff',
  },
  stateBanner: {
    padding: '10px 14px',
    borderRadius: 12,
    background: '#ecfdf5',
    color: '#0f766e',
    fontSize: 13,
    fontWeight: 600,
  },
  fieldHint: {
    margin: '6px 0 0',
    fontSize: 12,
    color: '#94a3b8',
    lineHeight: 1.4,
  },
  jobList: { display: 'grid', gap: 10 },
  jobCard: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    padding: 14,
    borderRadius: 18,
    border: '1.5px solid #e2e8f0',
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.05)',
  },
  checkBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: '2px solid #cbd5e1',
    flexShrink: 0,
    marginTop: 2,
    fontWeight: 800,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
  },
  jobBody: {
    flex: 1,
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    minWidth: 0,
  },
  jobCompany: { fontWeight: 800, fontSize: 16, color: '#0f172a' },
  jobPost: { marginTop: 2, fontSize: 14, color: '#334155', fontWeight: 600 },
  jobLoc: { marginTop: 6, fontSize: 12, color: '#64748b', lineHeight: 1.4 },
  jobMeta: { marginTop: 6, fontSize: 12, color: '#475569', lineHeight: 1.4 },
  viewDetails: { marginTop: 8, fontSize: 12, fontWeight: 800, color: '#0d9488' },
  hintCard: {
    background: '#fff',
    borderRadius: 16,
    padding: 20,
    textAlign: 'center',
    color: '#64748b',
  },
  stickyBar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    gap: 10,
    padding: '12px 16px calc(12px + env(safe-area-inset-bottom))',
    background: 'rgba(255,255,255,0.96)',
    backdropFilter: 'blur(12px)',
    borderTop: '1px solid #e2e8f0',
    boxShadow: '0 -10px 40px rgba(15, 23, 42, 0.08)',
    zIndex: 20,
    maxWidth: 480,
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  stickySecondary: {
    flex: '0 0 28%',
    padding: '15px 8px',
    borderRadius: 16,
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#334155',
    fontWeight: 700,
    fontSize: 15,
    cursor: 'pointer',
  },
  stickyPrimary: {
    flex: 1,
    padding: '15px 8px',
    borderRadius: 16,
    border: 'none',
    background: 'linear-gradient(135deg, #0d9488, #0f766e)',
    color: '#fff',
    fontWeight: 800,
    fontSize: 15,
    cursor: 'pointer',
  },
  successBox: {
    background: '#fff',
    borderRadius: 20,
    padding: 22,
    marginBottom: 12,
    boxShadow: '0 8px 30px rgba(16, 185, 129, 0.15)',
    border: '1px solid #a7f3d0',
    textAlign: 'center',
  },
  successIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: '#10b981',
    color: '#fff',
    fontSize: 24,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
  },
  sectionTitle: { margin: '0 0 14px', fontSize: 16, fontWeight: 800, color: '#0f172a' },
  contactCard: {
    background: '#f0fdfa',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
  },
  phoneInline: { color: '#0f766e', fontWeight: 800, textDecoration: 'none' },
  outlineBtn: {
    padding: '12px 18px',
    borderRadius: 14,
    border: '2px solid #0d9488',
    background: '#fff',
    color: '#0f766e',
    fontWeight: 700,
    cursor: 'pointer',
  },
  primaryLinkBtn: {
    display: 'inline-block',
    padding: '14px 20px',
    borderRadius: 14,
    background: '#0d9488',
    color: '#fff',
    fontWeight: 800,
    textDecoration: 'none',
  },
  loadingCard: {
    background: '#fff',
    borderRadius: 20,
    padding: 32,
    textAlign: 'center',
    marginTop: 40,
  },
  spinner: {
    width: 36,
    height: 36,
    border: '3px solid #e2e8f0',
    borderTopColor: '#0d9488',
    borderRadius: '50%',
    margin: '0 auto',
    animation: 'spin 0.8s linear infinite',
  },
  emptyCard: {
    background: '#fff',
    borderRadius: 20,
    padding: 28,
    marginTop: 24,
    textAlign: 'center',
  },
  sub: { color: '#64748b', lineHeight: 1.5 },
};

export default JobInterestForm;
